import { useState, useContext, useEffect, useRef } from "react";
import dayjs from "dayjs";
import Title from "../title";
import EventContent from "./event-content";
import EventFeedback from "./event-feedback";
import Toast from "../toast";
import { useQuery } from "@tanstack/react-query";
import UserContext from "../../context/user";
import AppError from "../app-error";
import { getTranslatedField } from "../../utils/lang";

const EventBody = ({ event, labels, appLang }) => {
  const { callApi } = useContext(UserContext);
  const [eventNavigation, setEventNavigation] = useState();
  const [error, setError] = useState();
  const [cancelledToast, setCancelledToast] = useState();
  const [reservation, setReservation] = useState();
  const locationRef = useRef();

  const { isFetching: isFetchingReservation, isFetched } = useQuery(
    ["event-registration", event.id],
    () => callApi(`/events/${event.id}/registrations?offset=0&limit=10`),
    {
      enabled: !!event.id,
      select: (data) => data?.list[0],
      onSuccess: (data) => {
        if (data?.id) {
          setEventNavigation({
            url: data?.status === "REGISTERED" ? "booked" : "waiting-list",
          });
          setReservation(data);
        }
      },
      onError: (error) => setError(error),
    }
  );

  const { isFetching: isFetchingStages, data: stages } = useQuery(
    ["events-stages", event.id],
    () => callApi(`/events/${event.id}/stages?offset=0&limit=100`),
    {
      enabled: !eventNavigation && !!event.id && isFetched,
      select: (data) =>
        data?.list
          ?.filter(
            (item) =>
              dayjs().isAfter(dayjs(Number(item?.registrationStart))) &&
              dayjs().isBefore(dayjs(Number(item?.registrationEnd)))
          )
          ?.sort((a, b) =>
            dayjs(Number(a?.stageStart)).isBefore(dayjs(Number(b?.stageStart)))
              ? -1
              : dayjs(Number(a?.stageStart)).isAfter(dayjs(Number(b?.stageStart)))
              ? 1
              : 0
          )
          ?.map((stage) => ({
            ...stage,
            // Available seats on FE are refined byadding 1 (or 2 if a guest is added) seats (representing the users' booking) if a reservation in status booked is present
            ...(reservation &&
            reservation.status === "REGISTERED" &&
            reservation.stage.id === stage.id
              ? {
                  availableSeats:
                    typeof stage.availableSeats === "number" &&
                    stage.availableSeats + (reservation?.guestFullName ? 2 : 1),
                }
              : {}),
          })),
      onError: (error) => setError(error),
    }
  );

  useEffect(() => {
    const currentLocation = JSON.stringify(eventNavigation);
    if (locationRef.current !== currentLocation) {
      locationRef.current = currentLocation;
      window.scrollTo(0, 0);
    }
  }, [eventNavigation]);

  return error ? (
    <AppError labels={labels} />
  ) : isFetchingReservation ? (
    <div className="loading-spinner mt" />
  ) : (
    <div className="event">
      <Title
        showStepper={false}
        handleNavigation={
          {
            "stage-detail": () => setEventNavigation(null),
          }[eventNavigation?.url]
        }
      >
        {{
          "stage-detail": getTranslatedField(
            stages?.find((stage) => stage?.id === Number(eventNavigation?.id)),
            appLang,
            "title"
          ),
          booked: labels?.EVENT_BANNER_TITLE_BOOKED || "Your seat has been booked!",
          "waiting-list":
            labels?.EVENT_BANNER_TITLE_WAITING_LIST || "You've been signed up to the waiting list",
        }[eventNavigation?.url] || event?.title}
      </Title>
      {["booked", "waiting-list"].includes(eventNavigation?.url) ? (
        <EventFeedback
          reservation={reservation}
          labels={labels}
          appLang={appLang}
          eventNavigation={eventNavigation}
          setEventNavigation={setEventNavigation}
          setCancelledToast={setCancelledToast}
          eventId={event.id}
          setReservation={setReservation}
          event={event}
        />
      ) : isFetchingStages ? (
        <div className="loading-spinner mt" />
      ) : (
        <EventContent
          event={event}
          stages={stages}
          labels={labels}
          appLang={appLang}
          reservation={reservation}
          eventNavigation={eventNavigation}
          setEventNavigation={setEventNavigation}
          setReservation={setReservation}
        />
      )}
      {!!cancelledToast && (
        <Toast
          message={
            labels?.EVENT_CANCELLED_TOAST_MESSAGE || "Your booking has been successfully cancelled"
          }
          setToastVisibility={setCancelledToast}
        />
      )}
    </div>
  );
};

export default EventBody;
